import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MapPin, Plus, X } from 'lucide-react';
import type { Address } from '@/data/types';
import { COUNTRIES } from '@/lib/country';
import { useT } from '@/lib/i18n';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  title: string;
  initial?: Address;
  onSave: (a: Address) => void;
  onGeocode?: (a: Address) => Promise<{ lat: number; lng: number } | null>;
  onApplyToObject?: (a: Address) => void;
}

export function AddressDialog({
  open,
  onOpenChange,
  title,
  initial,
  onSave,
  onGeocode,
  onApplyToObject,
}: Props) {
  const t = useT();
  const [a, setA] = useState<Address>(initial ?? { country: 'CH' });
  const [busy, setBusy] = useState(false);

  const set = <K extends keyof Address>(k: K, v: Address[K]) =>
    setA(prev => ({ ...prev, [k]: v }));

  const phoneRows = a.phones && a.phones.length ? a.phones : [''];
  const setPhones = (next: string[]) => set('phones', next);
  const setPhoneAt = (i: number, v: string) =>
    setPhones(phoneRows.map((p, idx) => (idx === i ? v : p)));
  const addPhone = () => setPhones([...phoneRows, '']);
  const removePhoneAt = (i: number) =>
    setPhones(phoneRows.filter((_, idx) => idx !== i));

  const clean = (addr: Address): Address => ({
    ...addr,
    phones: (addr.phones ?? []).map(p => p.trim()).filter(Boolean),
  });
  const commit = (addr: Address) => onSave(clean(addr));

  const handleGeocode = async () => {
    if (!onGeocode) return;
    setBusy(true);
    try {
      const r = await onGeocode(a);
      if (r) {
        commit(a);
        onOpenChange(false);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{t('address.enterDetails')}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div>
            <Label>{t('address.companyName')}</Label>
            <Input
              value={a.company ?? ''}
              onChange={e => set('company', e.target.value)}
            />
          </div>
          <div>
            <Label>{t('address.attn')}</Label>
            <Input
              value={a.attn ?? ''}
              onChange={e => set('attn', e.target.value)}
            />
          </div>
          <div>
            <Label>{t('address.street')}</Label>
            <Input
              data-testid="addr-street"
              value={a.street ?? ''}
              onChange={e => set('street', e.target.value)}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div>
              <Label>{t('address.zip')}</Label>
              <Input
                data-testid="addr-zip"
                value={a.zip ?? ''}
                onChange={e => set('zip', e.target.value)}
              />
            </div>
            <div className="col-span-2">
              <Label>{t('address.city')}</Label>
              <Input
                data-testid="addr-city"
                value={a.city ?? ''}
                onChange={e => set('city', e.target.value)}
              />
            </div>
          </div>
          <div>
            <Label>{t('common.country')}</Label>
            <select
              className="flex h-9 w-full rounded-md border border-input bg-background text-foreground px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              value={a.country ?? ''}
              onChange={e => set('country', e.target.value)}
            >
              <option value="" className="bg-background text-foreground">
                {t('address.select')}
              </option>
              {COUNTRIES.map(c => (
                <option
                  key={c.code}
                  value={c.code}
                  className="bg-background text-foreground"
                >
                  {c.name} ({c.code})
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label>{t('address.phone')}</Label>
            <div className="space-y-2">
              {phoneRows.map((p, i) => (
                <div key={i} className="flex gap-2">
                  <Input
                    data-testid={`addr-phone-${i}`}
                    value={p}
                    onChange={e => setPhoneAt(i, e.target.value)}
                  />
                  {phoneRows.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 shrink-0"
                      onClick={() => removePhoneAt(i)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                data-testid="addr-add-phone"
                onClick={addPhone}
              >
                <Plus className="h-4 w-4" />
                {t('address.addPhone')}
              </Button>
            </div>
          </div>
          <div>
            <Label>{t('address.email')}</Label>
            <Input
              value={a.email ?? ''}
              onChange={e => set('email', e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          {onGeocode && (
            <Button
              type="button"
              variant="outline"
              onClick={handleGeocode}
              disabled={busy}
            >
              <MapPin className="h-4 w-4" />
              {t('address.geocodeSave')}
            </Button>
          )}
          {onApplyToObject && (
            <Button
              type="button"
              data-testid="addr-apply-object"
              onClick={() => {
                onApplyToObject(clean(a));
                onOpenChange(false);
              }}
            >
              {t('address.applyToObject')}
            </Button>
          )}
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => {
              commit(a);
              onOpenChange(false);
            }}
          >
            {t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
